import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Select from 'components/UI/Select';
import { getAddressByPostalCode } from 'modules/clients/actions';
import { YEARS_AT_ADDRESS_OPTIONS } from 'constants/digitalLoanQuestionnaire';
import { formatPostalCode, isValidPostalCode, requiresProofOfAddress } from '../utils/locationHelpers';
import { isAddressStepValid } from '../utils/questionnaireValidation';
import {
  OTHER_ADDRESS_OPTION,
  coloniesForCity,
  colonyHasCatalogData,
  emptyAddressLookupPatch,
  mapCityOptions,
  mapLabelOptions,
} from '../utils/addressLookup';
import ProofOfAddressUpload from './ProofOfAddressUpload';

/** Une colonias de todos los municipios del CP; el valor es el nombre (string). */
export function flattenColonyOptions(cities = []) {
  return mapLabelOptions(
    (cities || []).flatMap((city) => city.colonies || []),
  );
}

const LookupComboField = ({
  name,
  label,
  options,
  value,
  onSelect,
  onManualChange,
  onEnterManual,
  disabled = false,
  loading = false,
  otherLabel = OTHER_ADDRESS_OPTION,
  hasCatalogData,
}) => {
  const [manualMode, setManualMode] = useState(false);
  const catalogAvailable = hasCatalogData ?? options.length > 0;
  const hasApiOptions = options.length > 0;
  const showManualInput = !loading && (
    !catalogAvailable || manualMode || value === otherLabel
  );
  const selectDisabled = disabled
    || loading
    || (hasApiOptions && options.length === 1);
  const matchedValue = options.some((option) => option.value === value)
    ? value
    : '';
  const selectValue = matchedValue
    || (options.length === 1 ? options[0].value : '');
  const selectOptions = options.length <= 1
    || options.some((option) => option.value === otherLabel)
    ? options
    : [...options, { value: otherLabel, label: otherLabel }];

  const handleSelect = (nextValue) => {
    if (nextValue === otherLabel) {
      setManualMode(true);
      if (onEnterManual) {
        onEnterManual();
        return;
      }
      onManualChange('');
      return;
    }
    setManualMode(false);
    onSelect(nextValue);
  };

  if (showManualInput) {
    return (
      <Input
        className='mb-4'
        name={`${name}Manual`}
        label={label}
        placeholder={label}
        value={value === otherLabel ? '' : (value || '')}
        onChange={onManualChange}
        disabled={disabled}
        required
      />
    );
  }

  return (
    <Select
      className='mb-4'
      name={name}
      label={label}
      options={selectOptions}
      value={loading ? '' : selectValue}
      onChange={handleSelect}
      disabled={selectDisabled}
      loading={loading}
      canBeEmpty={catalogAvailable && options.length !== 1}
      required
    />
  );
};

LookupComboField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onManualChange: PropTypes.func.isRequired,
  onEnterManual: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  otherLabel: PropTypes.string,
  hasCatalogData: PropTypes.bool,
};

const ApplicabilityAddressScreen = ({
  literals,
  questionnaire,
  onAddressChange,
  onProofChange,
  onNext,
  onBack,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [proofFileName, setProofFileName] = useState('');
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [colonyLookupLoading, setColonyLookupLoading] = useState(false);
  const [colonyLookupFailed, setColonyLookupFailed] = useState(false);
  const lookedUpCpRef = useRef(null);
  const showProof = requiresProofOfAddress(questionnaire.locationType);

  const yearsOptions = useMemo(() => (
    YEARS_AT_ADDRESS_OPTIONS.map((option) => ({
      value: option.value,
      label: literals.yearsOptions[option.labelKey],
    }))
  ), [literals.yearsOptions]);

  const { address } = questionnaire;
  const stateName = address.stateName || address.state || '';
  const municipalityName = address.municipalityName || address.municipality || '';
  const colonyName = address.colonyName || address.colony || '';
  const colonyOptions = useMemo(
    () => coloniesForCity(cityOptions, municipalityName),
    [cityOptions, municipalityName],
  );
  const colonyCatalogAvailable = colonyHasCatalogData(
    cityOptions,
    municipalityName,
  );
  const otherLabel = literals.otherOption || OTHER_ADDRESS_OPTION;

  useEffect(() => {
    const file = questionnaire.proofOfAddress;
    if (!(file instanceof Blob)) {
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      setProofFileName('');
      return undefined;
    }

    const isImage = String(file.type || '').startsWith('image/');
    setProofFileName(file.name || '');
    if (!isImage) {
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return url;
    });

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [questionnaire.proofOfAddress]);

  useEffect(() => {
    if (!showProof && questionnaire.proofOfAddress) {
      onProofChange(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProof]);

  useEffect(() => {
    let cancelled = false;
    const postalCode = address.postalCode;

    if (!isValidPostalCode(postalCode)) {
      if (lookedUpCpRef.current) {
        onAddressChange(emptyAddressLookupPatch());
      }
      lookedUpCpRef.current = null;
      setStateOptions([]);
      setCityOptions([]);
      setColonyLookupLoading(false);
      setColonyLookupFailed(false);
      return undefined;
    }

    const previousCp = lookedUpCpRef.current;
    if (previousCp && previousCp !== postalCode) {
      onAddressChange(emptyAddressLookupPatch());
    }
    lookedUpCpRef.current = postalCode;
    setColonyLookupLoading(true);
    setColonyLookupFailed(false);

    dispatch(getAddressByPostalCode(postalCode)).then((response) => {
      if (cancelled) return;

      setColonyLookupLoading(false);

      if (!response) {
        setStateOptions([]);
        setCityOptions([]);
        setColonyLookupFailed(true);
        return;
      }

      const nextStates = mapLabelOptions(response.states || []);
      const nextCities = mapCityOptions(response.cities || []);
      setStateOptions(nextStates);
      setCityOptions(nextCities);
      setColonyLookupFailed(false);

      const patch = {};
      if (nextStates.length === 1) {
        patch.state = nextStates[0].value;
        patch.stateName = nextStates[0].value;
      }
      if (nextCities.length === 1) {
        patch.municipality = nextCities[0].value;
        patch.municipalityName = nextCities[0].value;
        const colonies = nextCities[0].colonies || [];
        if (colonies.length === 1) {
          patch.colony = colonies[0].value;
          patch.colonyName = colonies[0].value;
        }
      }
      if (Object.keys(patch).length) {
        onAddressChange(patch);
      }
    });

    return () => {
      cancelled = true;
    };
  // Solo reconsultar cuando cambia el CP.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.postalCode, dispatch]);

  const handleProofSelected = (file) => {
    onProofChange(file);
  };

  const handleMunicipalitySelect = (value) => {
    onAddressChange({
      municipality: value,
      municipalityName: value,
      colony: '',
      colonyName: '',
    });
  };

  const canContinue = disabled || isAddressStepValid(questionnaire);

  return (
    <div className='full-screen-container digital-loan-questionnaire digital-loan-questionnaire--address'>
      <div className='screen-content'>
        <h1 className='digital-loan-questionnaire__title text-primary font-bold text-center mb-2'>
          {literals.title}
        </h1>
        <p className='digital-loan-questionnaire__required-note mb-4'>{literals.requiredNote}</p>
        <p className='font-bold mb-2'>{literals.addressQuestion}</p>

        <Input
          className='mb-4'
          name='street'
          label={literals.streetLabel}
          placeholder={literals.streetLabel}
          value={address.street}
          onChange={(street) => onAddressChange({ street })}
          disabled={disabled}
          required
        />
        <Input
          className='mb-4'
          name='exteriorNumber'
          label={literals.exteriorLabel}
          placeholder={literals.exteriorPlaceholder}
          value={address.exteriorNumber}
          onChange={(exteriorNumber) => onAddressChange({ exteriorNumber })}
          disabled={disabled}
          required
        />
        <Input
          className='mb-4'
          label={literals.interiorLabel}
          placeholder={literals.interiorPlaceholder}
          value={address.interiorNumber}
          onChange={(interiorNumber) => onAddressChange({ interiorNumber })}
          disabled={disabled}
        />
        <Input
          className='mb-4'
          label={literals.postalLabel}
          value={address.postalCode}
          onChange={(value) => onAddressChange({ postalCode: formatPostalCode(value) })}
          disabled
          required
        />

        <LookupComboField
          name='state'
          label={literals.stateLabel}
          options={stateOptions}
          value={stateName}
          onSelect={(nextState) => onAddressChange({
            state: nextState,
            stateName: nextState,
          })}
          onEnterManual={() => onAddressChange({
            state: '',
            stateName: '',
            municipality: '',
            municipalityName: '',
            colony: '',
            colonyName: '',
          })}
          onManualChange={(nextState) => onAddressChange({
            state: nextState,
            stateName: nextState,
          })}
          disabled={disabled}
          loading={colonyLookupLoading}
          otherLabel={otherLabel}
        />
        <LookupComboField
          name='municipality'
          label={literals.municipalityLabel}
          options={cityOptions}
          value={municipalityName}
          onSelect={handleMunicipalitySelect}
          onEnterManual={() => onAddressChange({
            municipality: '',
            municipalityName: '',
            colony: '',
            colonyName: '',
          })}
          onManualChange={(nextMunicipality) => onAddressChange({
            municipality: nextMunicipality,
            municipalityName: nextMunicipality,
          })}
          disabled={disabled}
          loading={colonyLookupLoading}
          otherLabel={otherLabel}
        />
        <LookupComboField
          name='colony'
          label={literals.colonyLabel}
          options={colonyOptions}
          value={colonyName}
          onSelect={(nextColony) => onAddressChange({
            colony: nextColony,
            colonyName: nextColony,
          })}
          onEnterManual={() => onAddressChange({
            colony: '',
            colonyName: '',
          })}
          onManualChange={(nextColony) => onAddressChange({
            colony: nextColony,
            colonyName: nextColony,
          })}
          disabled={disabled}
          loading={colonyLookupLoading}
          otherLabel={otherLabel}
          hasCatalogData={colonyCatalogAvailable}
        />

        {colonyLookupFailed && (
          <p className='digital-loan-address-lookup-error mb-3'>
            {literals.addressLookupError}
          </p>
        )}

        <p className='font-bold mb-2'>{literals.yearsQuestion}</p>
        <Select
          className='mb-6'
          label={literals.yearsPlaceholder}
          options={yearsOptions}
          value={address.yearsAtAddress}
          onChange={(yearsAtAddress) => onAddressChange({ yearsAtAddress })}
          disabled={disabled}
          canBeEmpty
          required
        />

        {showProof && (
          <ProofOfAddressUpload
            literals={literals.proof}
            fileName={proofFileName || questionnaire.proofOfAddress?.name}
            previewUrl={previewUrl}
            disabled={disabled}
            onFileSelected={handleProofSelected}
          />
        )}
      </div>
      <div className='screen-actions'>
        <Button text={literals.next} disabled={!canContinue} onClick={onNext} />
        <Button text={literals.back} type='secondary' onClick={onBack} />
      </div>
    </div>
  );
};

ApplicabilityAddressScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  questionnaire: PropTypes.object.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  onProofChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ApplicabilityAddressScreen;
